import { InvoiceParams } from 'models/Invoice'
import moment from 'moment'

import { onlyNumber } from 'utils'

/**
 * Detecção do caso em que registrar recebimento manual com R$ 0,00 queima a nota
 * fiscal da cobrança.
 *
 * Quando a billing é paga, o backend dispara `after_commit :issue_nfse` →
 * `BillingCreateNfseWorker`, que chama `NfseScheduled#create_nfse`. Com
 * `amount_type = amount_paid` o valor da nota é `billing.amount_paid` — zero. A
 * nota nasce zerada e `Nfse#schedule_issue` a marca como `failed`, gravando o
 * `nfse_id` no agendamento. A partir daí `create_nfse` e o worker retornam cedo
 * (`billing.nfse.present?`) e a fatura NUNCA mais emite nota, mesmo corrigindo o
 * valor pago depois.
 *
 * O front não consegue pagar sem emitir: quem dispara a emissão é o model. A
 * única saída é cancelar o agendamento ANTES do pagamento.
 */

// Policies que emitem no momento em que a fatura é paga.
const POLICIES_ON_PAYMENT = ['on_billing_paid', 'on_billing_issued']

// Policies agendadas por data: só emitem se a data de emissão já chegou.
// Espelha `issue_date_reached?` do BillingCreateNfseWorker.
const POLICIES_BY_DATE = [
  'on_before_due_date',
  'on_after_due_date',
  'custom_schedule'
]

export type ZeroAmountNfseContext = {
  /** uuid do agendamento — ausente significa que não há nota programada. */
  scheduledUuid?: string | null
  /** `amount_type` do agendamento; só `amount_paid` depende do valor pago. */
  amountType?: string | null
  /** `nfse_policy` da billing — a mesma que o worker consulta. */
  nfsePolicy?: string | null
  /** `issue_date` do agendamento, usado só pelas policies por data. */
  issueDate?: string | null
  /** Nota já criada (por qualquer caminho): o backend não criaria outra. */
  hasIssuedNfse: boolean
}

/**
 * O campo de valor é uma string mascarada ("0,00", "1.234,56"). Vazio conta como
 * zero — o backend faz `params[:amount_paid].to_f`, que também vira 0.0.
 */
export const isZeroAmountPaid = (amountPaid?: string | null): boolean => {
  if (!amountPaid) return true

  return Number(onlyNumber(amountPaid)) === 0
}

/**
 * A emissão aconteceria agora, com o valor pago como base? Espelha as condições
 * do BillingCreateNfseWorker para o status `paid`.
 */
export const willIssueNfseOnPayment = (
  context?: ZeroAmountNfseContext | null
): boolean => {
  if (!context) return false
  if (!context.scheduledUuid) return false
  if (context.hasIssuedNfse) return false
  if (context.amountType !== 'amount_paid') return false

  const policy = context.nfsePolicy

  if (!policy) return false
  if (POLICIES_ON_PAYMENT.includes(policy)) return true

  if (POLICIES_BY_DATE.includes(policy)) {
    if (!context.issueDate) return false

    return moment(context.issueDate, 'YYYY-MM-DD').isSameOrBefore(
      moment(),
      'day'
    )
  }

  // no_nfse e on_demand não emitem sozinhas no pagamento.
  return false
}

export const shouldWarnZeroAmountNfse = (
  amountPaid?: string | null,
  context?: ZeroAmountNfseContext | null
): boolean => isZeroAmountPaid(amountPaid) && willIssueNfseOnPayment(context)

/**
 * Monta o contexto a partir do payload de `/v1/billings/:uuid/details`.
 *
 * `nfsePolicy` vem da billing (e não do agendamento) porque é essa que o
 * BillingCreateNfseWorker consulta. O agendamento serve de fallback.
 *
 * Atenção: no caso `per_cycle` o agendamento vive no Installment e não é
 * serializado aqui — `nfsesScheduled` vem indefinido e o aviso não dispara.
 * Non-goal conhecido (ver docs/prd/baixa-manual-valor-zero-nfse.md).
 */
export const buildZeroAmountNfseContext = (
  invoice?: InvoiceParams | null
): ZeroAmountNfseContext => {
  const scheduled = invoice?.nfsesScheduled

  return {
    scheduledUuid: scheduled?.uuid ?? null,
    amountType: scheduled?.amount_type ?? null,
    nfsePolicy: invoice?.nfsePolicy ?? scheduled?.nfse_policy ?? null,
    issueDate: scheduled?.issue_date ?? null,
    hasIssuedNfse: Boolean(scheduled?.nfse || invoice?.nfse_uuid)
  }
}
